import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import BookmarkIcon from "@mui/icons-material/Bookmark";
import PaymentIcon from "@mui/icons-material/Payment";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import CategoryIcon from "@mui/icons-material/Category";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import { add_fixed_assets } from "../../../services/assets_services";

/**
 * Props attendues:
 * - open: boolean
 * - onClose: function
 * - selectedStockProduct: object (produit sélectionné depuis le stock)
 *    Exemples de champs utilisés (adapte selon ta structure):
 *      product_code, product_reference, product_name, product_category, product_price,
 *      product_supplier (id fournisseur), product_quantity (quantité dispo)
 *
 * - suppliers: array (optionnel si tu veux afficher le fournisseur)
 * - assetCategories: array [{id, name}] OU catégories (si tu as un référentiel immo)
 * - directions: array (optionnel) [{id, name}]
 * - departments: array (optionnel) [{id, name}]
 * - onCreated: callback(result) (optionnel) appelé après création
 */
const Add_fixed_asset_modal = ({
  open_fixed_asset_modal,
  onClose,
  suppliers = [],
  assetCategories = [],
  directions = [],
  departments = [],
  onCreated,
}) => {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user-info"));
    } catch (e) {
      return null;
    }
  }, []);

  const user_id = user?.id;

  const [waiting, setWaiting] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [warningText, setWarningText] = useState("");

  // Champs FixedAsset
  const [product_code, setProductCode] = useState("");
  const [product_reference, setProductReference] = useState("");
  const [supplier_id, setSupplierId] = useState(null);

  const [asset_label, setAssetLabel] = useState("");
  const [asset_category, setAssetCategory] = useState("null");

  // quantité = nombre d’unités à créer (DB => 1 ligne/unité)
  const [quantity, setQuantity] = useState(1);

  const [unit_value, setUnitValue] = useState("");
  const [currency, setCurrency] = useState("MAD");
  const [acquisition_date, setAcquisitionDate] = useState(""); // YYYY-MM-DD
  const [invoice_number, setInvoiceNumber] = useState("");
  const [request_code, setRequestCode] = useState("");

  const [direction, setDirection] = useState("null");
  const [department, setDepartment] = useState("null");

  // Numéros de série (1 par unité) : textarea multi-lignes
  const [serialsText, setSerialsText] = useState("");

  // Si tu veux lier à une transaction stock, tu peux le passer depuis selectedStockProduct
  const [stock_transaction_id, setStockTransactionId] = useState(null);

  // Pré-remplissage à l’ouverture / changement produit
  useEffect(() => {
   //  console.log(open_fixed_asset_modal);

    if (!open_fixed_asset_modal) return;

    setErrorText("");
    setWarningText("");

    const p = open_fixed_asset_modal || {};

    setProductCode(p.product_code || "");
    setProductReference(p.product_reference || "");
    setSupplierId(
      p.supplier !== undefined && p.supplier !== null
        ? p.supplier
        : null
    );
    setAssetCategory(p.product_category !== undefined && p.product_category !== null
      ? p.product_category
      : "null");

      

    // libellé par défaut = nom produit
    setAssetLabel(p.product_name || "");

    // valeur unitaire par défaut = prix produit si dispo
    setUnitValue(
      p.product_price !== undefined && p.product_price !== null ? String(p.product_price) : ""
    );

    // quantité par défaut = 1 (unité)
    setQuantity(1);

    // optionnel si tu as l’id de transaction stock
    setStockTransactionId(p.stock_transaction_id || null);

    // reset champs business
    setAcquisitionDate("");
    setInvoiceNumber("");
    setRequestCode("");
    setDirection("null");
    setDepartment("null");
    setSerialsText("");
  }, [open_fixed_asset_modal]);

  const selectedSupplierLabel = useMemo(() => {
    if (!supplier_id) return "";
    const s = suppliers.find((x) => String(x.id) === String(supplier_id));
    return s ? `${s.parentCompany || ""}${s.fullName ? " -- " + s.fullName : ""}` : "";
  }, [supplier_id, suppliers]);

  // helper : parse serials (textarea)
  const serial_numbers = useMemo(() => {
    const lines = serialsText
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    return lines;
  }, [serialsText]);

  const totalPreview = useMemo(() => {
    const q = Number(quantity || 1);
    const u = Number(unit_value || 0);
    if (Number.isNaN(q) || Number.isNaN(u)) return "";
    return (q * u).toFixed(2);
  }, [quantity, unit_value]);

  async function handleAddFixedAssets() {
    setErrorText("");
    setWarningText("");

    // validations UI (minimum)
    if (!product_code || !asset_label) {
      setErrorText("Champs invalides : code produit et libellé sont obligatoires.");
      return;
    }
    const q = Number(quantity);
    if (!q || q < 1) {
      setErrorText("La quantité doit être >= 1.");
      return;
    }

    // Option: si l’utilisateur a saisi des serials => doit matcher quantité
    if (serial_numbers.length > 0 && serial_numbers.length !== q) {
      setErrorText("Le nombre de numéros de série doit être égal à la quantité.");
      return;
    }

    // Option: warning si pas de serials sur une quantité > 1
    if (q > 1 && serial_numbers.length === 0) {
      setWarningText(
        "Attention : vous créez plusieurs unités sans numéros de série. Confirmez si c'est OK."
      );
      // On ne bloque pas, c’est un warning seulement
    }

    setWaiting(true);

    try {
      const payload = {
        stock_transaction_id: stock_transaction_id,
        product_code: product_code,
        product_reference: product_reference || null,
        supplier_id: supplier_id || null,
        request_code: request_code || null,
        invoice_number: invoice_number || null,

        asset_label: asset_label,
        asset_category: asset_category === "null" ? null : Number(asset_category),

        // quantité = nombre d’unités à créer (backend va splitter en 1 ligne/unité)
        quantity: q,

        unit_value: unit_value === "" ? null : Number(unit_value),
        currency: currency || null,
        acquisition_date: acquisition_date || null,

        direction: direction === "null" ? null : Number(direction),
        department: department === "null" ? null : Number(department),

        // optionnel : serial_numbers (si tu as activé ça dans ton backend)
        serial_numbers: serial_numbers.length > 0 ? serial_numbers : null,

        created_by: user_id, // même si backend le récupère via Auth, ça ne gêne pas (sera ignoré si tu filtres)
      };

      const res = await add_fixed_assets(payload);

      if (onCreated) onCreated(res.data);

      setWaiting(false);
      onClose();
    } catch (err) {
     //  console.log(err?.response || err);
      setWaiting(false);
      setErrorText(
        err?.response?.data?.message ||
        "Erreur lors de la création des immobilisations. Vérifiez les champs."
      );
    }
  }

  return (
    <Dialog fullWidth maxWidth="md" open={open_fixed_asset_modal} onClose={onClose}>
      <DialogTitle>Déclarer un produit du stock comme Immobilisation</DialogTitle>
      <DialogContent>
        <div className="add_product_modal_infos">
          <div className="inputs_container">
            <TextField
              variant="standard"
              label="Code produit"
              value={product_code}
              disabled
              className="input_form"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BookmarkIcon className="my_icon" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              variant="standard"
              label="Référence"
              value={product_reference}
              disabled
              className="input_form"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BookmarkIcon className="my_icon" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              variant="standard"
              label="Fournisseur"
              value={selectedSupplierLabel || (supplier_id ? String(supplier_id) : "")}
              disabled
              className="input_form"
            />
          </div>

          {/* Champs immo */}
          <div className="inputs_container">
            <TextField
              variant="standard"
              label="Libellé immobilisation"
              value={asset_label}
              onChange={(e) => setAssetLabel(e.target.value)}
              style={{ flex: 1, marginRight: 20}}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AssignmentTurnedInIcon className="my_icon" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl variant="standard" style={{ minWidth: 260 }}>
              <InputLabel>Catégorie immobilisation</InputLabel>
              <Select
                value={asset_category}
                onChange={(e) => setAssetCategory(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon className="my_icon" />
                  </InputAdornment>
                }
              >
                <MenuItem value="null" disabled>
                  Sélectionner catégorie
                </MenuItem>

                {assetCategories.map((c, idx) => (
                  <MenuItem key={"asset-cat-" + idx} value={c.id_category}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="inputs_container">
            <TextField
              variant="standard"
              label="Quantité (unités à créer)"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ProductionQuantityLimitsIcon className="my_icon" />
                  </InputAdornment>
                ),
              }}
              helperText="1 ligne = 1 unité (la quantité crée N lignes)"
            />

            <TextField
              variant="standard"
              label="Valeur unitaire"
              type="number"
              value={unit_value}
              onChange={(e) => setUnitValue(e.target.value)}
              style={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PaymentIcon className="my_icon" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              variant="standard"
              label="Devise"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              style={{ minWidth: 160 }}
            />

          </div>

          <div className="inputs_container">
            <TextField
              variant="standard"
              label="Date d'acquisition"
              type="date"
              value={acquisition_date}
              onChange={(e) => setAcquisitionDate(e.target.value)}
              style={{ minWidth: 260 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              variant="standard"
              label="N° facture"
              value={invoice_number}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              style={{ minWidth: 260 }}
            />

            <TextField
              variant="standard"
              label="Référence DA/BC (request_code)"
              value={request_code}
              onChange={(e) => setRequestCode(e.target.value)}
              style={{ minWidth: 260 }}
            />

            {/* <TextField
              variant="standard"
              label="Total estimé (preview)"
              value={totalPreview ? `${totalPreview} ${currency || ""}` : ""}
              disabled
              style={{ minWidth: 220 }}
            /> */}
          </div>

          {/* Direction / Department (si tu veux) */}
          <div className="inputs_container">
            <FormControl variant="standard" style={{ minWidth: 260 }}>
              <InputLabel>Direction</InputLabel>
              <Select value={direction} onChange={(e) => setDirection(e.target.value)}>
                <MenuItem value="null" disabled>
                  Sélectionner direction
                </MenuItem>
                {directions.map((d, idx) => (
                  <MenuItem key={"dir-" + idx} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

           {/*  <FormControl variant="standard" style={{ minWidth: 260 }}>
              <InputLabel>Département</InputLabel>
              <Select value={department} onChange={(e) => setDepartment(e.target.value)}>
                <MenuItem value="null" disabled>
                  Sélectionner département
                </MenuItem>
                {departments.map((d, idx) => (
                  <MenuItem key={"dep-" + idx} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div style={{ minWidth: 260 }}></div> */}
          </div>

          {/* Serial numbers */}
          <div style={{ width: "100%", marginTop: 22 }}>
            <TextField
              label="Numéros de série (1 par ligne - optionnel)"
              variant="outlined"
              multiline
              minRows={6}
              value={serialsText}
              onChange={(e) => setSerialsText(e.target.value)}
              style={{ width: "100%" }}
              placeholder={"SN001\nSN002\nSN003"}
              helperText={`Si renseigné : ${serial_numbers.length} numéro(s) détecté(s). Doit = Quantité (${quantity || 1}).`}
            />
          </div>

          {/* Messages */}
          <div style={{ marginTop: 12 }}>
            {errorText ? <p className="error">{errorText}</p> : null}
            {warningText ? <p className="warning">{warningText}</p> : null}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          className="submit_but cancel_but"
          startIcon={<CancelIcon className="my_icon" />}
          onClick={onClose}
        >
          Annuler
        </Button>

        <Button
          variant="contained"
          className="submit_but valide_but"
          startIcon={<AddIcon className="my_icon" />}
          onClick={handleAddFixedAssets}
          disabled={waiting}
        >
          Créer Immobilisation(s)
        </Button>
      </DialogActions>

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={waiting}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default Add_fixed_asset_modal;
